import React from 'react';

export const TransformationVisual = ({ isVisible = false }) => {
  return (
    <div className="relative w-full">
      {/* Before State - Chaotic */}
      <div className="flex items-stretch gap-4 lg:gap-8">
        {/* Before */}
        <div className={`flex-1 relative transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a2435] to-[#0f1a2a] rounded-sm" />
          <div className="relative p-8 lg:p-10">
            {/* Chaotic Visual */}
            <div className="relative h-48 mb-8 overflow-hidden">
              <svg viewBox="0 0 200 150" className="w-full h-full">
                {/* Scattered, chaotic lines */}
                <g className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                  {[...Array(12)].map((_, i) => (
                    <line
                      key={i}
                      x1={20 + Math.random() * 60}
                      y1={20 + Math.random() * 110}
                      x2={120 + Math.random() * 60}
                      y2={20 + Math.random() * 110}
                      stroke="#5a7fa8"
                      strokeWidth="0.5"
                      opacity={0.3 + Math.random() * 0.3}
                      style={{
                        transform: `rotate(${Math.random() * 30 - 15}deg)`,
                        transformOrigin: 'center'
                      }}
                    />
                  ))}
                </g>
                
                {/* Scattered nodes */}
                <g className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                  <circle cx="40" cy="30" r="4" fill="#5a7fa8" opacity="0.4" />
                  <circle cx="150" cy="50" r="3" fill="#5a7fa8" opacity="0.5" />
                  <circle cx="80" cy="80" r="5" fill="#5a7fa8" opacity="0.3" />
                  <circle cx="120" cy="110" r="4" fill="#5a7fa8" opacity="0.6" />
                  <circle cx="60" cy="120" r="3" fill="#5a7fa8" opacity="0.4" />
                  <circle cx="170" cy="90" r="4" fill="#5a7fa8" opacity="0.5" />
                  <circle cx="30" cy="70" r="3" fill="#5a7fa8" opacity="0.4" />
                </g>
                
                {/* Fragmented center */}
                <g className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                  <rect x="85" y="60" width="30" height="30" fill="none" stroke="#c9a87c" strokeWidth="1" opacity="0.4" transform="rotate(15 100 75)" />
                  <rect x="90" y="65" width="20" height="20" fill="none" stroke="#c9a87c" strokeWidth="0.5" opacity="0.3" transform="rotate(-10 100 75)" />
                </g>
              </svg>
              
              {/* Floating disruption particles */}
              <div className="absolute top-4 left-8 w-1 h-1 bg-[#5a7fa8] rounded-full opacity-40 float" />
              <div className="absolute top-12 right-12 w-1.5 h-1.5 bg-[#5a7fa8] rounded-full opacity-30 float-reverse" />
              <div className="absolute bottom-8 left-16 w-1 h-1 bg-[#c9a87c] rounded-full opacity-50 float-slow" />
            </div>
            
            <h3 className="text-xs text-[#6b7c93] uppercase tracking-[0.2em] mb-6">Before</h3>
            <ul className="space-y-4">
              {['Reactive leadership', 'Cognitive overload', 'Emotional leakage', 'Leadership from effort'].map((item, i) => (
                <li key={i} className={`flex items-start gap-3 text-[#b8c5d4] transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`} style={{ transitionDelay: `${800 + i * 100}ms` }}>
                  <span className="w-1 h-1 rounded-full bg-[#5a7fa8] mt-2.5 opacity-60" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Transformation Arrow */}
        <div className="hidden lg:flex items-center justify-center px-4">
          <div className={`relative transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
            <svg viewBox="0 0 80 40" className="w-20 h-10">
              <defs>
                <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#5a7fa8" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#c9a87c" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#c9a87c" stopOpacity="1" />
                </linearGradient>
              </defs>
              <line x1="0" y1="20" x2="60" y2="20" stroke="url(#arrowGradient)" strokeWidth="1" />
              <polygon points="60,14 75,20 60,26" fill="#c9a87c" />
            </svg>
            <div className="absolute inset-0 blur-sm bg-gradient-to-r from-transparent via-[#c9a87c] to-[#c9a87c] opacity-20" />
          </div>
        </div>

        {/* After */}
        <div className={`flex-1 relative transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a2435] to-[#0f1a2a] rounded-sm" />
          {/* Subtle glow */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-[#c9a87c] opacity-5 blur-[60px] rounded-full" />
          
          <div className="relative p-8 lg:p-10">
            {/* Ordered Visual */}
            <div className="relative h-48 mb-8 overflow-hidden">
              <svg viewBox="0 0 200 150" className="w-full h-full">
                {/* Ordered, aligned lines */}
                <g className={`transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                  <line x1="40" y1="35" x2="160" y2="35" stroke="#5a7fa8" strokeWidth="0.5" opacity="0.3" />
                  <line x1="40" y1="55" x2="160" y2="55" stroke="#5a7fa8" strokeWidth="0.5" opacity="0.4" />
                  <line x1="40" y1="75" x2="160" y2="75" stroke="#c9a87c" strokeWidth="1" opacity="0.6" />
                  <line x1="40" y1="95" x2="160" y2="95" stroke="#5a7fa8" strokeWidth="0.5" opacity="0.4" />
                  <line x1="40" y1="115" x2="160" y2="115" stroke="#5a7fa8" strokeWidth="0.5" opacity="0.3" />
                </g>
                
                {/* Aligned nodes */}
                <g className={`transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                  <circle cx="100" cy="75" r="20" fill="none" stroke="#c9a87c" strokeWidth="1" opacity="0.6" />
                  <circle cx="100" cy="75" r="8" fill="#c9a87c" opacity="0.8" className="breathe" />
                </g>
                
                {/* Connection points */}
                <g className={`transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                  <circle cx="40" cy="75" r="3" fill="#5a7fa8" opacity="0.6" />
                  <circle cx="160" cy="75" r="3" fill="#5a7fa8" opacity="0.6" />
                  <circle cx="100" cy="35" r="2" fill="#5a7fa8" opacity="0.4" />
                  <circle cx="100" cy="115" r="2" fill="#5a7fa8" opacity="0.4" />
                </g>
              </svg>
              
              {/* Calm floating particles */}
              <div className="absolute top-1/2 left-1/4 w-1 h-1 bg-[#c9a87c] rounded-full opacity-40 float-slow" />
              <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-[#c9a87c] rounded-full opacity-40 float-slow" style={{ animationDelay: '-3s' }} />
            </div>
            
            <h3 className="text-xs text-[#c9a87c] uppercase tracking-[0.2em] mb-6">After</h3>
            <ul className="space-y-4">
              {['Calm authority', 'Emotional composure', 'Coherent decisions', 'Leadership from clarity'].map((item, i) => (
                <li key={i} className={`flex items-start gap-3 text-[#f5f5f0] transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`} style={{ transitionDelay: `${1000 + i * 100}ms` }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c9a87c] mt-2" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransformationVisual;
